import Modal from "@components/Modal";
import useAnalytics from "@hooks/useAnalytics";
import useApiFetch from "@hooks/useApiFetch";
import useClientContext, { setCurrentEntry, setModal } from "@hooks/useClientContext";
import useEntryMutation, { EntryResponse } from "@hooks/useEntryMutation";
import { SubmitHandler, useForm } from "react-hook-form";
import { media, publicBasePath, pxToRem } from "src/styles/styleUtils";
import styled, { css } from "styled-components";
import Button, { SecondaryButton } from "./Button";
import {
  Divider,
  ErrorSpan,
  FlexCol,
  FormRow,
  Input,
  InputDescription,
  InputWrapper,
  Label,
} from "./Form";
import { ModalContent, ModalHeader, ModalSubHeader } from "./Modal";

interface EntryFormInputs {
  name: string;
  avatar: number;
  terms_and_conditions: boolean;
}

function EntryModal() {
  const [{ openModal, currentEntry }, dispatch] = useClientContext();
  const { fetchApiEndpoint } = useApiFetch();
  const { trackEvent } = useAnalytics();

  const isEditMode = openModal.name === "EDIT_ENTRY";
  const entryId = currentEntry?.id;
  const entryName = currentEntry?.name;
  const defaultAvatarId = isEditMode ? currentEntry?.avatar_id ?? 50 : 50;
  const avatarIdCollection = [
    30, 31, 32, 50, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
    22, 23, 24, 25, 26, 27, 28, 29,
  ];

  const shouldRenderModal = ["CREATE_ENTRY", "EDIT_ENTRY"].includes(openModal.name);
  const modalTitle = `${isEditMode ? "Edit" : "Create an"} Entry`;
  const modalSubmitText = `${isEditMode ? "Save" : "Create New"} Entry`;
  const defaultError = "Invalid entry, please try again.";

  function createEntry(data: EntryFormInputs) {
    const body = { ...data };
    body.avatar = Number(body.avatar);
    return fetchApiEndpoint<EntryResponse>("/api/v1/chase/entries.json", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  function editEntry(data: EntryFormInputs) {
    const body = { ...data };
    body.avatar = Number(body.avatar);
    return fetchApiEndpoint<EntryResponse>(`/api/v1/chase/entries/${entryId}.json`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }
  const mutation = useEntryMutation(isEditMode ? editEntry : createEntry);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EntryFormInputs>();

  const onSubmit: SubmitHandler<EntryFormInputs> = (data) => {
    mutation.mutate(data, {
      onSuccess: (data) => {
        dispatch(setModal(null));
        dispatch(setCurrentEntry(data.entry));
        trackEvent(`entries:${isEditMode ? "edit" : "new"}`);
      },
    });
  };

  return (
    shouldRenderModal && (
      <Modal aria-label="Create Entry Dialog" css={modalCSS}>
        <ModalHeader>{modalTitle}</ModalHeader>
        {!isEditMode && (
          <ModalSubHeader>You may create up to three separate entries for the game.</ModalSubHeader>
        )}
        <ModalContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormRow>
              <FlexCol>
                <Label htmlFor="name">Entry Name</Label>
                <Input
                  type="text"
                  defaultValue={isEditMode ? entryName : ""}
                  {...register("name", {
                    required: "* Entry Name is required",
                    minLength: {
                      value: 3,
                      message: "* Entry Name must be 3 or more characters",
                    },
                  })}
                />
                {errors.name && <ErrorSpan>{errors.name?.message || defaultError}</ErrorSpan>}
              </FlexCol>
            </FormRow>

            <FormRow>
              <FlexCol>
                <Label>Entry Icon</Label>
                <AvatarContainer>
                  {avatarIdCollection.map((id) => (
                    <label key={id}>
                      <input
                        type="radio"
                        {...register("avatar", {
                          required: "* Please select an entry icon",
                        })}
                        value={id}
                        defaultChecked={id === defaultAvatarId}
                      />
                      <img
                        src={`${publicBasePath}/images/avatar/${id}.png`}
                        alt="Entry Avatar"
                        draggable="false"
                        width="48"
                        height="48"
                      />
                    </label>
                  ))}
                </AvatarContainer>
                {errors.avatar && <ErrorSpan>{errors.avatar?.message || defaultError}</ErrorSpan>}
              </FlexCol>
            </FormRow>

            {!isEditMode && (
              <FormRow>
                <FlexCol>
                  <Divider />
                  <InputWrapper>
                    <Input
                      type="checkbox"
                      {...register("terms_and_conditions", {
                        required: "* Terms and Conditions must be accepted",
                      })}
                    />
                    <label htmlFor="terms_and_conditions">Terms and Conditions</label>
                  </InputWrapper>
                  <InputDescription>
                    I have read and agree to NASCAR Playoffs Grid Challenge rules.
                  </InputDescription>
                  {errors.terms_and_conditions && (
                    <ErrorSpan>{errors.terms_and_conditions?.message || defaultError}</ErrorSpan>
                  )}
                </FlexCol>
              </FormRow>
            )}

            <FormRow className="call-to-action">
              <InputWrapper>
                <SecondaryButton type="button" onClick={() => dispatch(setModal(null))}>
                  Cancel
                </SecondaryButton>
                <Button type="submit">{modalSubmitText}</Button>
              </InputWrapper>
              {mutation.error && (
                <ErrorSpan>
                  {mutation.error instanceof Error
                    ? mutation.error.message
                    : "Server Error: Please try again."}
                </ErrorSpan>
              )}
            </FormRow>
          </form>
        </ModalContent>
      </Modal>
    )
  );
}

export default EntryModal;

const modalCSS = css`
  [data-reach-dialog-content] {
    max-width: ${pxToRem(580)};
  }
  &[data-reach-dialog-overlay] {
    z-index: 999;
  }
`;

const AvatarContainer = styled.div`
  display: flex;
  flex-flow: row wrap;

  label {
    margin: 0 ${pxToRem(8)} ${pxToRem(8)} 0;
  }

  [type="radio"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;

    :checked ~ img {
      border-color: ${({ theme }) => theme.gold};
    }

    &:not(:disabled):focus-visible ~ img {
      outline: ${pxToRem(1.5)} solid ${({ theme }) => theme.gold};
    }
  }

  img {
    padding: ${pxToRem(8)};
    border: ${pxToRem(2)} solid transparent;
    cursor: pointer;
  }

  ${media.mobile} {
    justify-content: center;
  }
`;
